import React, { useEffect, useState } from "react";
import { failed, success } from "../../common/Toastify";
import { useFormik } from "formik";
import { QuestionsFormikObjEdit } from "./Constant";
import { editQuestion } from "../../api/Questionnaire";
import { Modal } from "react-bootstrap";
import { QuestionForm } from "./QuestionForm";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { validation, conditionalValidation } from "../../../utils/constants";

const SPECIAL_MANDATORY_TAGS = new Set([
  "CHANNEL",
  "TELEPHONE",
  "REASON",
  "CALLER",
  "LOCATION",
  "RELATIONSHIP",
  "SELFCALLING",
  "CONSENT",
  "INCLINATION",
  "COMPLIANCE",
]);

const EditQuestion = ({ modalShow, handleShow, refreshTable, isReadOnly }) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const [tagValue, setTagValue] = useState();
  const [isModalTag, setIsModalTag] = useState();
  const navigate = useNavigate();

  const channelDataMap = { id: Date.now(), text: "Phone" };

  const formik = useFormik({
    ...QuestionsFormikObjEdit,
    validationSchema: Yup.object({
      category: conditionalValidation(
        Yup.string()
        .matches(/^$|^\S+.*/, "Only blankspaces is not valid."),
        {
          validation,
          label: "Category",
          mandatory: true
        }),
      question: conditionalValidation(
        Yup.string()
        .matches(/^$|^\S+.*/, "Only blankspaces is not valid.")
        .max(400, "Must be 400 characters or less"),
        {
          validation,
          label: "Question",
          mandatory: true
        }),
      answerType: conditionalValidation(
        Yup.string()
        .matches(/^$|^\S+.*/, "Only blankspaces is not valid."),
        {
          validation,
          label: "Answer Type",
          mandatory: true
        }),
      note: conditionalValidation(
        Yup.string()
          .when("answerType", (answerType, schema) => {
            if (answerType === "freeText" || answerType === "singleFreeText")
              return schema
                .matches(/^$|^\S+.*/, "Only blankspaces is not valid.");
            return schema;
          })
          .matches(/^$|^\S+.*/, "Only blankspaces is not valid.")
          .max(250, "Must be 250 characters or less"),
        {
          validation,
          label: "Note"
        }),
      required: Yup.boolean(),
      options: Yup.array().when(["answerType", "tag"], (answerType, tag, schema) => {
        if (answerType !== "freeText" && answerType !== "singleFreeText" && tag !== "CHANNEL") {
          return schema.of(
            Yup.object().shape({
              text: conditionalValidation(
                Yup.string(),
                {
                  validation,
                  mandatory: true,
                  label: "Answer"
                })
            })
          );
        }
        if (tag === "CHANNEL") {
          return schema.of(
            Yup.object().shape({
              text: conditionalValidation(
                Yup.string(),
                {
                  validation,
                  label: "Chat/Video",
                  mandatory: true
                })
            })
          );
        }
        return schema;
      }),
    }),
    onSubmit: (values) => {
      setBtnLoading(true);

      values.category = values?.category?.split("%")?.[1];

      const effectiveTag =
        tagValue !== undefined && isModalTag === undefined
          ? tagValue
          : tagValue === undefined && isModalTag !== undefined
          ? ""
          : tagValue !== undefined && isModalTag !== undefined
          ? tagValue
          : (modalShow?.tag ?? values.tag ?? "");

      values.tag = effectiveTag;

      if (SPECIAL_MANDATORY_TAGS.has(effectiveTag)) {
        values.required = true;
      }

      if (effectiveTag === "CHANNEL") {
        const hasPhone = (values.options || []).some((o) => o.text === "Phone");
        if (!hasPhone) {
          values.options = [...(values.options || []), channelDataMap];
        }
      }

      editQuestion(modalShow?.id, values)
        .then((res) => {
          refreshTable?.();
          handleShow?.();
          success(res.message);
        })
        .catch((res) =>
          failed(res?.response?.data?.message || res?.response?.data?.error || res.message)
        )
        .finally(() => setBtnLoading(false));
    },
  });

  useEffect(() => {
    if (modalShow?.id) {
      formik.setFieldValue("category", modalShow?.category || "");
      formik.setFieldValue("question", modalShow?.question || "");
      formik.setFieldValue("answerType", modalShow?.answerType || "freeText");
      formik.setFieldValue("note", modalShow?.note || "");
      formik.setFieldValue("required", modalShow?.required ?? false);
      formik.setFieldValue("tag", modalShow?.tag || "");
      formik.setFieldValue("createdAt", modalShow?.createdAt || "");
      formik.setFieldValue("updatedAt", modalShow?.updatedAt || "");
      formik.setFieldValue("orgId", modalShow?.orgId || "");
      formik.setFieldValue("id", modalShow?.id || "");
      modalShow?.options?.length &&
        formik.setFieldValue(
          "options",
          modalShow?.options || QuestionsFormikObjEdit?.initialValues?.options
        );

      setTagValue(modalShow?.tag ?? "");
      setIsModalTag(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalShow]);

  if (!modalShow) return null;

  const handleQuestionTagValue = (value) => {
    if (typeof value === "string") {
      setTagValue(value);
      formik.setFieldValue("tag", value);
    }
    if (typeof value === "object") {
      return;
    }
  };

  const handleIsModalTagChange = (newIsModalTag) => {
    setIsModalTag(newIsModalTag);
    if (newIsModalTag) {
      setTagValue("");
      formik.setFieldValue("tag", "");
    }
  };

  return (
    <Modal
      show={modalShow}
      onHide={handleShow}
      size="xl"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="custom-dialog"
      backdrop={"static"}
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title id="contained-modal-title-vcenter">
          {isReadOnly ? "View" : "Edit"} Question
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {formik?.values?.category ? (
          <QuestionForm
            btnLoading={btnLoading}
            formik={formik}
            handleShow={handleShow}
            isCreate={false}
            handleQuestionTagValue={handleQuestionTagValue}
            isReadOnly={isReadOnly}
            refreshTable={refreshTable}
            modalShow={modalShow}
            onIsModalTagChange={handleIsModalTagChange}
            enableOrgChange={false}
          />
        ) : null}
      </Modal.Body>
    </Modal>
  );
};

export default EditQuestion;